import { useEffect, type ReactNode } from 'react'
import { AlertTriangle, Info, Loader2, X } from 'lucide-react'

export function Carte({
  titre,
  icone,
  actions,
  children,
  compte,
  className = '',
}: {
  titre?: ReactNode
  icone?: ReactNode
  actions?: ReactNode
  children: ReactNode
  compte?: number
  className?: string
}) {
  return (
    <section className={`carte ${className}`}>
      {titre && (
        <header className="titre-carte">
          <h2>
            {icone}
            {titre}
            {compte !== undefined && (
              <span className="text-[12px] font-normal text-brume">{compte}</span>
            )}
          </h2>
          {actions && <div className="flex items-center gap-1">{actions}</div>}
        </header>
      )}
      {children}
    </section>
  )
}

export function Pastille({
  ton = 'neutre',
  children,
  icone,
}: {
  ton?: 'neutre' | 'bleu' | 'valide' | 'attention' | 'critique'
  children: ReactNode
  icone?: ReactNode
}) {
  const tons = {
    neutre: 'bg-slate-100 text-ardoise',
    bleu: 'bg-bleu-50 text-bleu-700',
    valide: 'bg-valide-fond text-valide',
    attention: 'bg-attention-fond text-attention',
    critique: 'bg-critique-fond text-critique',
  }
  return (
    <span className={`pastille ${tons[ton]}`}>
      {icone}
      {children}
    </span>
  )
}

export function Chargement({ texte = 'Chargement…' }: { texte?: string }) {
  return (
    <div className="flex items-center justify-center gap-2 py-10 text-[13px] text-brume">
      <Loader2 className="h-4 w-4 animate-spin" />
      {texte}
    </div>
  )
}

export function EtatVide({
  texte,
  action,
  icone,
}: {
  texte: string
  action?: ReactNode
  icone?: ReactNode
}) {
  return (
    <div className="flex flex-col items-center gap-2 px-5 py-8 text-center">
      {icone && <div className="text-bleu-200">{icone}</div>}
      <p className="text-[13px] text-brume">{texte}</p>
      {action}
    </div>
  )
}

export function Message({
  ton = 'info',
  children,
}: {
  ton?: 'info' | 'attention' | 'critique'
  children: ReactNode
}) {
  const styles = {
    info: 'bg-bleu-50 text-bleu-700',
    attention: 'bg-attention-fond text-attention',
    critique: 'bg-critique-fond text-critique',
  }
  const Icone = ton === 'info' ? Info : AlertTriangle
  return (
    <div className={`flex items-start gap-2 rounded-lg px-3.5 py-2.5 text-[13px] ${styles[ton]}`}>
      <Icone className="mt-0.5 h-4 w-4 shrink-0" />
      <div className="flex-1">{children}</div>
    </div>
  )
}

export function Modale({
  titre,
  sousTitre,
  large = false,
  fermer,
  children,
  piedDePage,
}: {
  titre: string
  sousTitre?: ReactNode
  large?: boolean
  fermer: () => void
  children: ReactNode
  piedDePage?: ReactNode
}) {
  // Échap ferme la fenêtre : on travaille au clavier dans un dossier.
  useEffect(() => {
    const surTouche = (e: KeyboardEvent) => {
      if (e.key === 'Escape') fermer()
    }
    document.addEventListener('keydown', surTouche)
    return () => document.removeEventListener('keydown', surTouche)
  }, [fermer])

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-encre/25 p-4 py-10"
      onClick={fermer}
    >
      <div
        className={`apparition w-full ${large ? 'max-w-4xl' : 'max-w-xl'} rounded-2xl bg-white`}
        style={{ boxShadow: 'var(--shadow-flottant)' }}
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-start justify-between gap-4 px-6 pt-5 pb-4">
          <div>
            <h2 className="text-[16px] font-semibold text-encre">{titre}</h2>
            {sousTitre && <p className="mt-0.5 text-[13px] text-brume">{sousTitre}</p>}
          </div>
          <button className="bouton-discret -mr-2 -mt-1 px-2" onClick={fermer} aria-label="Fermer">
            <X className="h-4 w-4" />
          </button>
        </header>
        <div className="px-6 pb-5">{children}</div>
        {piedDePage && (
          <footer className="flex items-center justify-end gap-2 border-t border-trait px-6 py-4">
            {piedDePage}
          </footer>
        )}
      </div>
    </div>
  )
}

export function Champ({
  etiquette,
  children,
  aide,
  className = '',
}: {
  etiquette: string
  children: ReactNode
  aide?: string
  className?: string
}) {
  return (
    <label className={`block ${className}`}>
      <span className="etiquette">{etiquette}</span>
      {children}
      {aide && <span className="mt-1 block text-[11px] text-brume">{aide}</span>}
    </label>
  )
}
